<script setup lang="ts">
import type { ClickEvent } from '@autobid/ui/types/Vue'
import Menu from '@autobid/ui/components/elements/menu/Menu.vue'
import MenuLink from '@autobid/ui/components/elements/menu/MenuLink.vue'
import { computed, ref } from 'vue'

interface Option {
  href: string
  label: string
}

interface Tab {
  name: string
  label: string
  bindParams?: Record<string, string | number>
  options?: Option[]
}

interface Props {
  tabs: Tab[]
}

const props = defineProps<Props>()

const activeTab = ref<string>()
const tabOffset = ref(0)
const tabWidth = ref(0)
const commonMenuToggler = ref<HTMLSpanElement>()
const currentTabData = computed(() =>
  props.tabs.find(tab => tab.name === activeTab.value),
)

function handleOpenTab(event: ClickEvent, tabName: string) {
  let { left } = event.target.getClientRects()[0] ?? {}
  const sizeOfOffsetAndMenu = left + event.target.clientWidth

  if (sizeOfOffsetAndMenu > window.innerWidth) {
    // offset is just for a better look
    const OUT_OF_WINDOW_OFFSET = 20
    left -= sizeOfOffsetAndMenu - window.innerWidth + OUT_OF_WINDOW_OFFSET
  }

  activeTab.value = tabName
  commonMenuToggler.value?.click()
  tabOffset.value = left
  tabWidth.value = event.target.clientWidth
}
</script>

<template>
  <div>
    <Menu
      class="btns-nav-options !absolute"
      :class="{
        'z-0': !currentTabData,
        'z-30': currentTabData,
      }"
      button-class="opacity-0"
      :style="{
        width: `${tabWidth}px`,
        left: `${tabOffset}px`,
      }"
    >
      <span ref="commonMenuToggler">&nbsp;</span>

      <template #items>
        <template v-if="currentTabData?.options?.length">
          <MenuLink
            v-for="option in currentTabData.options"
            :key="option.href"
            :href="option.href"
          >
            {{ option.label }}
          </MenuLink>
        </template>
      </template>
    </Menu>

    <nav
      class="buttons-nav container relative z-20 mx-auto flex w-full gap-2 overflow-x-auto overflow-y-hidden whitespace-nowrap"
    >
      <template v-for="tab in tabs">
        <ElementsButtonsNavButton
          v-if="tab.options?.length"
          :key="`options${tab.name}`"
          :active="tab.name === activeTab"
          v-bind="tab.bindParams || {}"
          @click="handleOpenTab($event, tab.name)"
        >
          {{ tab.label }}
        </ElementsButtonsNavButton>

        <ElementsButton
          v-else
          :key="`btn${tab.name}`"
          class="min-w-[200px] text-xs"
          v-bind="tab.bindParams || {}"
        >
          {{ tab.label }}
        </ElementsButton>
      </template>
    </nav>
  </div>
</template>

<style lang="scss" scoped>
nav {
  // to avoid a blinking scroll during hover (when the scroll is not visible in the basic state)
  width: calc(100% + 10px);
  margin: 0 -5px;
  padding: 0 5px;
}
</style>

<style lang="scss">
.btns-nav-options {
  &:not([data-headlessui-state='open']) {
    & + nav {
      .rotate-180 {
        transform: rotate(0deg);
      }
    }
  }
}
</style>
